import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Chip, Autocomplete, TextField, IconButton, Tooltip, createFilterOptions } from '@mui/material';
import { Close, BlockOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { updateTaskBlockers } from '../redux/ducks/task';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { showAlert } from '../redux/slices/alerts';
import StatusChip from './StatusChip';

const SectionContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const BlockersList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
}));

const BlockerRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
}));

const BlockerName = styled(Typography)(({ theme }) => ({
    flexGrow: 1,
    fontSize: theme.typography.body2.fontSize,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const EmptyText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    fontSize: theme.typography.caption.fontSize,
}));

const collectDownstream = (taskId, allTasks) => {
    const downstream = new Set();
    const queue = [taskId];
    while (queue.length > 0) {
        const current = queue.shift();
        for (const t of allTasks) {
            const blockers = Array.isArray(t.blockedBy) ? t.blockedBy : [];
            const blockerIds = blockers.map((b) => (typeof b === 'string' ? b : b?._id)).filter(Boolean);
            if (blockerIds.includes(current) && !downstream.has(t._id)) {
                downstream.add(t._id);
                queue.push(t._id);
            }
        }
    }
    return downstream;
};

const blockerFilter = createFilterOptions({
    stringify: (option) => option?.name || '',
    trim: true,
});

const TaskBlockersSection = ({ task, projectTasks = [], canEdit = true, onUpdated }) => {
    const dispatch = useDispatch();
    const [busy, setBusy] = useState(false);
    const [pickerValue, setPickerValue] = useState(null);

    const blockers = useMemo(() => {
        const list = Array.isArray(task?.blockedBy) ? task.blockedBy : [];
        return list.map((b) => {
            if (typeof b === 'string') {
                const found = projectTasks.find((t) => t._id === b);
                return found || { _id: b, name: 'Unknown task' };
            }
            return b;
        });
    }, [task, projectTasks]);

    const candidateOptions = useMemo(() => {
        if (!task?._id) return [];
        const blockerIds = new Set(blockers.map((b) => b._id));
        const downstream = collectDownstream(task._id, projectTasks);
        const seen = new Set();
        return projectTasks.filter((t) => {
            if (!t || !t._id) return false;
            if (t._id === task._id) return false;
            if (t.isDeleted) return false;
            if (blockerIds.has(t._id)) return false;
            if (downstream.has(t._id)) return false;
            if (seen.has(t._id)) return false;
            seen.add(t._id);
            return true;
        });
    }, [task, projectTasks, blockers]);

    const applyUpdate = (add, remove, successMsg) => {
        if (busy) return;
        setBusy(true);
        dispatch(updateTaskBlockers(task._id, add, remove, (data) => {
            setBusy(false);
            if (data?.task) {
                if (typeof onUpdated === 'function') {
                    onUpdated(data.task);
                }
                dispatch(triggerRefresh({ triggerName: 'project' }));
                if (successMsg) {
                    dispatch(showAlert({ severity: 'success', message: successMsg }));
                }
            }
        }));
    };

    const handleAdd = (_event, value) => {
        if (!value || !value._id) return;
        setPickerValue(null);
        applyUpdate([value._id], [], 'Blocker added');
    };

    const handleRemove = (blockerId) => {
        applyUpdate([], [blockerId], 'Blocker removed');
    };

    return (
        <SectionContainer>
            <HeaderRow>
                <BlockOutlined fontSize="small" />
                <SectionTitle variant="subtitle2">Blocked By</SectionTitle>
            </HeaderRow>
            <BlockersList>
                {blockers.length === 0 ? (
                    <EmptyText>No blockers</EmptyText>
                ) : (
                    blockers.map((b) => (
                        <BlockerRow key={b._id}>
                            <BlockerName>{b.name || 'Unknown task'}</BlockerName>
                            {b.status && (
                                <StatusChip id={b._id} status={b.status} parentType="task" />
                            )}
                            {canEdit && (
                                <Tooltip title="Remove blocker">
                                    <span>
                                        <IconButton size="small" onClick={() => handleRemove(b._id)} disabled={busy}>
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </BlockerRow>
                    ))
                )}
            </BlockersList>
            {canEdit && (
                <Autocomplete
                    size="small"
                    options={candidateOptions}
                    value={pickerValue}
                    onChange={handleAdd}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, val) => option?._id === val?._id}
                    filterOptions={blockerFilter}
                    renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                            {option.name}
                        </li>
                    )}
                    disabled={busy || candidateOptions.length === 0}
                    renderInput={(params) => (
                        <TextField {...params} placeholder={candidateOptions.length === 0 ? 'No eligible tasks' : 'Add blocker…'} />
                    )}
                />
            )}
        </SectionContainer>
    );
};

export default TaskBlockersSection;
